// FILE: src/pages/ReconciliationListPage.jsx
//
// Reconciliations list — redesigned in the app's modern design language
// (scoped CSS under .rlpg-root, Geist typography, sticky header, card table).
// All behavior preserved from the previous version:
//   - URL query params drive the view: ?accountId, ?page (1-based), ?limit
//     (defaults omitted from the URL; allowed limits 25/50/100, default 25)
//   - GET /accounts?limit=1000 (dropdown), GET /reconciliations {page,limit,accountId?}
//   - Draft rows: Edit + Finalize (window.confirm → POST /reconciliations/finalize/:id)
//   - Posted/Reopened rows: View + Re-Open (Re-Open only when userRole === "Admin")
//   - Out-of-order finalize rejection surfaces the server's futureReconciliations dates
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import MuiPagination from "@mui/material/Pagination";
import API from "../api";
import { formatDate } from "../utils/formatDate";

const DEFAULT_LIMIT = 25;
const ALLOWED_LIMITS = [25, 50, 100];

function parseListParams(searchParams) {
  const rawPage = parseInt(searchParams.get("page"), 10);
  const page = Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1;
  const rawLimit = parseInt(searchParams.get("limit"), 10);
  const limit = ALLOWED_LIMITS.includes(rawLimit) ? rawLimit : DEFAULT_LIMIT;
  const accountFilter = searchParams.get("accountId") || "all";
  return { page, limit, accountFilter };
}

function fmtMoney(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return num.toFixed(2);
}
function fmtMoneySigned(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  if (Math.abs(num) < 0.005) return "0.00";
  const sign = num > 0 ? "+" : "−";
  return `${sign}${Math.abs(num).toFixed(2)}`;
}

const RECON_LIST_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@400;450;500;600;700&family=Geist+Mono:wght@400;500;600&display=swap');

/* MUI papers reuse .rlpg-root for tokens — never inherit page sizing */
.MuiPaper-root.rlpg-root { min-height: 0; background: #fff; }
.rlpg-root {
  --rlpg-primary: #1E90FF;
  --rlpg-primary-soft: #E8F3FF;
  --rlpg-warn: #FFC107;
  --rlpg-warn-soft: #FFF7DC;
  --rlpg-warn-deep: #B86E00;
  --rlpg-success: #2E7D32;
  --rlpg-success-soft: #E8F5E9;
  --rlpg-danger: #C62828;
  --rlpg-danger-soft: #FDECEC;
  --rlpg-text-1: #111827;
  --rlpg-text-2: #374151;
  --rlpg-text-3: #6B7280;
  --rlpg-text-4: #9CA3AF;
  --rlpg-border-1: #ECECEC;
  --rlpg-border-2: #DDDDDD;
  --rlpg-surface: #FFFFFF;
  --rlpg-surface-sub: #FAFAFA;
  --rlpg-surface-sub-2: #F4F4F5;
  --rlpg-radius-sm: 6px;
  --rlpg-radius-md: 8px;
  --rlpg-radius-lg: 12px;
  --rlpg-shadow-sm: 0 1px 2px rgba(15, 23, 42, 0.04);
  --rlpg-navbar-h: 60px;

  font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-feature-settings: 'cv11', 'ss03';
  color: var(--rlpg-text-1);
  font-size: 13.5px;
  line-height: 1.5;
  letter-spacing: -0.005em;
}
.rlpg-root * { box-sizing: border-box; }
.rlpg-root.rlpg-page {
  background: var(--rlpg-surface-sub);
  min-height: 100vh;
  padding: 0;
}

/* ----- Sticky top ----- */
.rlpg-sticky-top {
  position: sticky;
  top: var(--rlpg-navbar-h);
  z-index: 30;
  background: var(--rlpg-surface-sub);
  border-bottom: 1px solid var(--rlpg-border-1);
}
.rlpg-header {
  padding: 20px 24px 12px;
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 20px; flex-wrap: wrap;
}
.rlpg-title {
  font-size: 22px; font-weight: 600; letter-spacing: -0.02em;
  margin: 0 0 4px; color: var(--rlpg-text-1);
}
.rlpg-subtitle { font-size: 12.5px; color: var(--rlpg-text-3); }
.rlpg-actions { display: flex; align-items: center; gap: 8px; }
.rlpg-btn {
  display: inline-flex; align-items: center; gap: 7px;
  height: 34px; padding: 0 14px;
  font-family: inherit; font-size: 12.5px; font-weight: 500;
  border-radius: var(--rlpg-radius-md);
  border: 1px solid transparent; cursor: pointer;
  transition: all 0.15s ease; white-space: nowrap;
}
.rlpg-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.rlpg-btn-primary { background: var(--rlpg-primary); color: #fff; }
.rlpg-btn-primary:hover:not(:disabled) { background: #1A7DD9; }
.rlpg-btn-ghost {
  background: var(--rlpg-surface); color: var(--rlpg-text-2);
  border-color: var(--rlpg-border-2);
}
.rlpg-btn-ghost:hover:not(:disabled) { background: var(--rlpg-surface-sub-2); }
.rlpg-btn-success-soft {
  background: var(--rlpg-success-soft); color: var(--rlpg-success);
  border-color: #B7DBBA;
}
.rlpg-btn-success-soft:hover:not(:disabled) { background: #DCF0DD; }
.rlpg-btn-sm { height: 28px; padding: 0 10px; font-size: 12px; }

/* ----- Filter bar ----- */
.rlpg-filterbar {
  display: flex; align-items: flex-end; gap: 10px; flex-wrap: wrap;
  padding: 14px 24px 16px;
  background: var(--rlpg-surface);
  border-top: 1px solid var(--rlpg-border-1);
}
.rlpg-filter-field { display: flex; flex-direction: column; gap: 4px; min-width: 280px; flex: 0 1 340px; }
.rlpg-filter-field label {
  font-size: 10.5px; font-weight: 500; text-transform: uppercase;
  letter-spacing: 0.04em; color: var(--rlpg-text-3);
}

/* MUI Autocomplete polish */
.rlpg-mui-ac .MuiOutlinedInput-root {
  font-family: 'Geist', sans-serif !important;
  font-size: 13px !important;
  padding: 2px 8px !important;
  min-height: 34px !important;
  background: #fff;
}
.rlpg-mui-ac .MuiOutlinedInput-notchedOutline {
  border-color: var(--rlpg-border-2) !important;
  border-radius: var(--rlpg-radius-md) !important;
}
.rlpg-mui-ac.Mui-focused .MuiOutlinedInput-notchedOutline {
  border-color: var(--rlpg-primary) !important;
}

/* ----- Body / card / table ----- */
.rlpg-body { padding: 18px 24px 32px; }
.rlpg-alert {
  margin-bottom: 14px;
  padding: 10px 14px;
  background: var(--rlpg-danger-soft);
  color: var(--rlpg-danger);
  border-left: 3px solid var(--rlpg-danger);
  border-radius: var(--rlpg-radius-sm);
  font-size: 13px;
  display: flex; align-items: flex-start; justify-content: space-between; gap: 8px;
  white-space: pre-line;
}
.rlpg-alert button {
  background: none; border: none; color: inherit; cursor: pointer;
  font-size: 16px; line-height: 1; padding: 0 4px; flex-shrink: 0;
}
.rlpg-card {
  background: var(--rlpg-surface);
  border: 1px solid var(--rlpg-border-1);
  border-radius: var(--rlpg-radius-lg);
  box-shadow: var(--rlpg-shadow-sm);
  overflow: hidden;
}
.rlpg-table-wrap { width: 100%; overflow-x: auto; }
.rlpg-table {
  width: 100%; border-collapse: separate; border-spacing: 0;
  font-size: 13px; min-width: 900px;
}
.rlpg-table thead th {
  background: var(--rlpg-surface);
  color: var(--rlpg-text-2);
  font-size: 11px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.04em;
  padding: 10px;
  border-bottom: 1px solid var(--rlpg-border-2);
  text-align: left; white-space: nowrap;
}
.rlpg-table thead th.r { text-align: right; }
.rlpg-table tbody td {
  padding: 11px 10px;
  border-bottom: 1px solid var(--rlpg-border-1);
  vertical-align: middle;
}
.rlpg-table tbody td.r {
  text-align: right;
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
}
.rlpg-table tbody td.mono {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 12.5px;
}
.rlpg-table tbody tr { transition: background 0.1s ease; }
.rlpg-table tbody tr:hover { background: var(--rlpg-surface-sub); }
.rlpg-acc-cell { display: flex; flex-direction: column; gap: 1px; min-width: 140px; }
.rlpg-acc-name { font-weight: 500; color: var(--rlpg-text-1); font-size: 12.5px; }
.rlpg-acc-no {
  font-family: 'Geist Mono', monospace;
  font-size: 10.5px; color: var(--rlpg-text-3);
}
.rlpg-val-pos { color: var(--rlpg-success); font-weight: 500; }
.rlpg-val-neg { color: var(--rlpg-danger); font-weight: 500; }
.rlpg-val-zero { color: var(--rlpg-text-3); }

/* Status chips */
.rlpg-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 2px 8px 2px 6px;
  font-size: 11px; font-weight: 500;
  border-radius: 999px; white-space: nowrap;
}
.rlpg-chip-dot { width: 6px; height: 6px; border-radius: 50%; }
.rlpg-chip-draft { background: var(--rlpg-warn-soft); color: var(--rlpg-warn-deep); border: 1px solid #F1D88A; }
.rlpg-chip-draft .rlpg-chip-dot { background: var(--rlpg-warn-deep); }
.rlpg-chip-posted { background: var(--rlpg-success-soft); color: var(--rlpg-success); border: 1px solid #B7DBBA; }
.rlpg-chip-posted .rlpg-chip-dot { background: var(--rlpg-success); }
.rlpg-chip-reopened { background: var(--rlpg-primary-soft); color: #0E5EAB; border: 1px solid #BBDCFB; }
.rlpg-chip-reopened .rlpg-chip-dot { background: var(--rlpg-primary); }

.rlpg-row-actions { display: flex; gap: 6px; justify-content: flex-end; }

/* States */
.rlpg-state { padding: 56px 32px; text-align: center; color: var(--rlpg-text-3); }
.rlpg-state-title { color: var(--rlpg-text-2); font-size: 14px; font-weight: 500; margin: 12px 0 4px; }
.rlpg-state-sub { font-size: 12.5px; color: var(--rlpg-text-3); max-width: 360px; margin: 0 auto; }

/* ----- Pagination footer ----- */
.rlpg-pagination-wrap {
  padding: 10px 14px;
  border-top: 1px solid var(--rlpg-border-1);
  background: var(--rlpg-surface);
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap;
}
.rlpg-pagination-info { font-size: 12px; color: var(--rlpg-text-3); }
.rlpg-pagination-controls { display: flex; align-items: center; gap: 12px; }
.rlpg-rows-select {
  height: 30px; padding: 0 8px;
  background: var(--rlpg-surface);
  border: 1px solid var(--rlpg-border-2);
  border-radius: var(--rlpg-radius-md);
  font-family: inherit; font-size: 12.5px; color: var(--rlpg-text-1);
}
.rlpg-pagination-wrap .MuiPagination-root .MuiPaginationItem-root {
  font-family: 'Geist', sans-serif;
  font-size: 12.5px;
  border-radius: var(--rlpg-radius-md);
}
.rlpg-pagination-wrap .MuiPaginationItem-root.Mui-selected {
  background: var(--rlpg-primary-soft);
  color: var(--rlpg-primary);
  font-weight: 600;
}
`;

function IconPlus({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function StatusChip({ status }) {
  if (status === "Draft") {
    return (
      <span className="rlpg-chip rlpg-chip-draft">
        <span className="rlpg-chip-dot" /> Draft
      </span>
    );
  }
  if (status === "Reopened") {
    return (
      <span className="rlpg-chip rlpg-chip-reopened">
        <span className="rlpg-chip-dot" /> Reopened
      </span>
    );
  }
  return (
    <span className="rlpg-chip rlpg-chip-posted">
      <span className="rlpg-chip-dot" /> {status}
    </span>
  );
}

export default function ReconciliationListPage() {
  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole");

  const [searchParams, setSearchParams] = useSearchParams();
  const { page: currentPage, limit, accountFilter } =
    parseListParams(searchParams);

  const [reconciliations, setReconciliations] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Bumped after a successful Finalize to refetch without touching the URL.
  const [refetchToken, setRefetchToken] = useState(0);

  // Feedback: snackbar for successes, dismissible banner for the
  // chronological-order rejection (too long for a snackbar).
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [blockBanner, setBlockBanner] = useState("");

  // Load account dropdown options once.
  useEffect(() => {
    let cancelled = false;
    API.get("/accounts?limit=1000")
      .then((res) => {
        if (!cancelled) setAccounts(res.data.accounts || []);
      })
      .catch((err) => {
        console.error("Failed to load accounts:", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load reconciliations whenever the URL filters or refetch token change.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    const params = { page: currentPage, limit };
    if (accountFilter !== "all") params.accountId = accountFilter;

    API.get("/reconciliations", { params })
      .then((res) => {
        if (cancelled) return;
        const { data, totalCount: tc, totalPages: tp } = res.data;
        setReconciliations(data || []);
        setTotalCount(tc || 0);
        setTotalPages(tp || 1);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load reconciliations:", err);
        setError("Error loading reconciliations");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentPage, limit, accountFilter, refetchToken]);

  const updateUrl = useCallback(
    (next) => {
      const params = new URLSearchParams();
      if (next.accountFilter && next.accountFilter !== "all") {
        params.set("accountId", next.accountFilter);
      }
      if (next.page > 1) params.set("page", String(next.page));
      if (next.limit !== DEFAULT_LIMIT) params.set("limit", String(next.limit));
      setSearchParams(params);
    },
    [setSearchParams]
  );

  const handleChangePage = (_, newPage) =>
    updateUrl({ accountFilter, page: newPage, limit });

  const handleChangeRowsPerPage = (e) =>
    updateUrl({
      accountFilter,
      page: 1,
      limit: parseInt(e.target.value, 10),
    });

  const handleFilterChange = (newId) =>
    updateUrl({ accountFilter: newId || "all", page: 1, limit });

  const handleCreate = () => navigate("/reconciliation/new");
  const handleEdit = (id) => navigate(`/reconciliation/edit/${id}`);
  const handleView = (id) => navigate(`/reconciliation/edit/${id}`);
  const handleReopen = (id) => navigate(`/reconciliation/reopen/${id}`);

  const handleFinalize = async (id) => {
    if (!window.confirm("Are you sure you want to finalize this reconciliation?"))
      return;
    try {
      const res = await API.post(`/reconciliations/finalize/${id}`);
      setSnackbar({
        open: true,
        message: res.data.message,
        severity: "success",
      });
      setBlockBanner("");
      // Refresh in place — keeps the current page and filter intact.
      setRefetchToken((t) => t + 1);
    } catch (err) {
      const errorMsg = err.response?.data?.message || "Failed to finalize";
      const futureRecons = err.response?.data?.futureReconciliations;

      if (futureRecons && futureRecons.length > 0) {
        const dates = futureRecons
          .map((r) => new Date(r.date).toLocaleDateString())
          .join(", ");
        setBlockBanner(
          `Cannot Finalize — Out of Order!\n` +
            `There are already finalized reconciliations for future dates: ${dates}\n` +
            `To finalize this reconciliation, you must first REOPEN those future reconciliations to maintain chronological order.\n` +
            `Reconciliations must be finalized in date order to ensure data integrity.`
        );
      } else {
        setSnackbar({ open: true, message: errorMsg, severity: "error" });
      }
    }
  };

  const accountOptions = useMemo(() => {
    const sorted = [...accounts].sort((a, b) =>
      a.accountName.localeCompare(b.accountName)
    );
    return [
      { _id: "all", accountName: "All Accounts", accountNo: "" },
      ...sorted,
    ];
  }, [accounts]);

  const selectedAccountOption = useMemo(
    () =>
      accountOptions.find((o) => o._id === accountFilter) || accountOptions[0],
    [accountOptions, accountFilter]
  );

  const shownFrom = totalCount === 0 ? 0 : (currentPage - 1) * limit + 1;
  const shownTo = Math.min(currentPage * limit, totalCount);

  return (
    <div className="rlpg-root rlpg-page">
      <style>{RECON_LIST_CSS}</style>

      {/* Sticky top */}
      <div className="rlpg-sticky-top">
        <div className="rlpg-header">
          <div>
            <h1 className="rlpg-title">Reconciliations</h1>
            <div className="rlpg-subtitle">
              {totalCount} {totalCount === 1 ? "record" : "records"} · newest
              date first — finalize the oldest Draft first
            </div>
          </div>
          <div className="rlpg-actions">
            <button
              type="button"
              className="rlpg-btn rlpg-btn-primary"
              onClick={handleCreate}
            >
              <IconPlus /> New Draft
            </button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="rlpg-filterbar">
          <div className="rlpg-filter-field">
            <label>Account</label>
            <Autocomplete
              className="rlpg-mui-ac"
              size="small"
              options={accountOptions}
              getOptionLabel={(opt) =>
                opt._id === "all"
                  ? opt.accountName
                  : `${opt.accountName} (${opt.accountNo})`
              }
              value={selectedAccountOption}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              onChange={(_, newVal) => handleFilterChange(newVal?._id)}
              renderInput={(params) => (
                <TextField {...params} placeholder="Filter by account…" />
              )}
            />
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="rlpg-body">
        {error && (
          <div className="rlpg-alert" role="alert">
            <span>{error}</span>
            <button onClick={() => setError("")} aria-label="Dismiss">
              ×
            </button>
          </div>
        )}
        {blockBanner && (
          <div className="rlpg-alert" role="alert">
            <span>{blockBanner}</span>
            <button onClick={() => setBlockBanner("")} aria-label="Dismiss">
              ×
            </button>
          </div>
        )}

        <div className="rlpg-card">
          {loading ? (
            <div className="rlpg-state">
              <CircularProgress size={28} />
              <div className="rlpg-state-title">Loading reconciliations…</div>
            </div>
          ) : reconciliations.length === 0 ? (
            <div className="rlpg-state">
              <div className="rlpg-state-title">No reconciliations found.</div>
              <div className="rlpg-state-sub">
                {accountFilter !== "all"
                  ? "Try a different account filter, or create a new draft."
                  : "Create your first draft to start reconciling an account."}
              </div>
            </div>
          ) : (
            <>
              <div className="rlpg-table-wrap">
                <table className="rlpg-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Account</th>
                      <th className="r">System Bal</th>
                      <th className="r">Actual Cash</th>
                      <th className="r">Variance</th>
                      <th>User</th>
                      <th>Status</th>
                      <th style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reconciliations.map((rec) => {
                      const v = Number(rec.variance);
                      const vCls =
                        !Number.isFinite(v) || Math.abs(v) < 0.005
                          ? "rlpg-val-zero"
                          : v > 0
                          ? "rlpg-val-pos"
                          : "rlpg-val-neg";
                      return (
                        <tr key={rec._id}>
                          <td className="mono">{formatDate(rec.date)}</td>
                          <td>
                            <div className="rlpg-acc-cell">
                              <span className="rlpg-acc-name">
                                {rec.account?.accountName || "N/A"}
                              </span>
                              {rec.account?.accountNo && (
                                <span className="rlpg-acc-no">
                                  {rec.account.accountNo}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="r">{fmtMoney(rec.systemBalance)}</td>
                          <td className="r">{fmtMoney(rec.actualCash)}</td>
                          <td className={`r ${vCls}`}>
                            {fmtMoneySigned(rec.variance)}
                          </td>
                          <td>{rec.user}</td>
                          <td>
                            <StatusChip status={rec.status} />
                          </td>
                          <td>
                            <div className="rlpg-row-actions">
                              {rec.status === "Draft" ? (
                                <>
                                  <button
                                    type="button"
                                    className="rlpg-btn rlpg-btn-ghost rlpg-btn-sm"
                                    onClick={() => handleEdit(rec._id)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="rlpg-btn rlpg-btn-success-soft rlpg-btn-sm"
                                    onClick={() => handleFinalize(rec._id)}
                                  >
                                    Finalize
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    className="rlpg-btn rlpg-btn-ghost rlpg-btn-sm"
                                    onClick={() => handleView(rec._id)}
                                  >
                                    View
                                  </button>
                                  {userRole === "Admin" && (
                                    <button
                                      type="button"
                                      className="rlpg-btn rlpg-btn-ghost rlpg-btn-sm"
                                      onClick={() => handleReopen(rec._id)}
                                    >
                                      Re-Open
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination footer */}
              <div className="rlpg-pagination-wrap">
                <span className="rlpg-pagination-info">
                  Showing {shownFrom}–{shownTo} of {totalCount} records
                </span>
                <div className="rlpg-pagination-controls">
                  <select
                    className="rlpg-rows-select"
                    value={limit}
                    onChange={handleChangeRowsPerPage}
                    aria-label="Rows per page"
                  >
                    {ALLOWED_LIMITS.map((l) => (
                      <option key={l} value={l}>
                        {l} rows
                      </option>
                    ))}
                  </select>
                  <MuiPagination
                    page={currentPage}
                    count={totalPages}
                    onChange={handleChangePage}
                    siblingCount={1}
                    boundaryCount={1}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
